import dayjs, { type OpUnitType } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat.js";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
import isBetween from "dayjs/plugin/isBetween";
import relativeTime from "dayjs/plugin/relativeTime";

import type { Timestamp } from "firebase/firestore";
import type { DateFormat } from "../plans/types";
import { getDaysInRange } from "./groupDates";

// Locales
import "dayjs/locale/sv";

dayjs.locale("en");
dayjs.extend(relativeTime);
dayjs.extend(isBetween);

export function setDayJsLocale(locale: string) {
  dayjs.locale(locale);
}

export function isTimeStampInThePast(timestamp?: Timestamp): boolean {
  try {
    return (timestamp?.toMillis() ?? 0) < Date.now();
  } catch (_e) {
    return false;
  }
}

export function getDayJs(timestamp?: Timestamp) {
  try {
    // @ts-expect-error
    return dayjs(timestamp.toDate()).fromNow();
  } catch (_e) {
    return "";
  }
}

export function getDayJsFromDate(date?: Date) {
  try {
    return dayjs(date).fromNow();
  } catch (_e) {
    return "";
  }
}

export function getDayJsDate(
  timestamp?: Timestamp | null,
  format?: DateFormat,
) {
  if (!timestamp) {
    return "";
  }
  return dayjs(timestamp.toDate()).format(
    format ? format.substring(0, 10) : "YYYY-MM-DD",
  ); // skip time
}

export function getDayJsDateAndTime(
  timestamp?: Timestamp | null,
  format?: DateFormat,
) {
  if (!timestamp) {
    return "";
  }
  return dayjs(timestamp.toDate()).format(format ? format : "YYYY-MM-DD HH:mm");
}

export function getDayJsDateAndTimeWithFormat(timestamp: Date, format: string) {
  return dayjs(timestamp).format(format);
}

// Extend dayjs with the customParseFormat plugin
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

export function formatDate(
  dateString: string | Date,
  skipYear?: boolean,
  skipMonth?: boolean,
): string {
  const date = dayjs(dateString, { format: "YYYY-MM-DD" });

  // Get the day of the month with suffix (e.g., "3rd")
  const dayOfMonth = date.format("Do");

  // Get the month name (e.g., "January")
  const month = date.format(skipYear ? "MMMM" : "MMM");

  // Get the year (e.g., "2024")
  const year = date.format("YYYY");

  return `${dayOfMonth}${skipMonth ? "" : ` ${month}`}${skipYear ? "" : ` ${year}`}`;
}

export function isSame(date1: Date, date2: Date, unitType: OpUnitType) {
  return dayjs(date1).isSame(date2, unitType);
}

export function isSameDay(date1: Date, date2: Date) {
  return dayjs(date1).isSame(date2, "day");
}

export function formatDateRange(range: string, _withDayDiff?: boolean): string {
  if (!range) {
    return "";
  }

  const [startDate, endDate] = range.split(" - ");

  const startReadable = formatDate(startDate, !!endDate);
  const endReadable = endDate ? formatDate(endDate) : undefined;

  // If the start and end dates are the same, return only the start date
  if (!endDate || startDate === endDate) {
    return startReadable;
  }

  const daysInRage = getDaysInRange(range);

  return `${startReadable} - ${endReadable}, ${daysInRage} days`;
}

export function areDatesWithinRange(
  selectedDates: string[],
  start: Date,
  end: Date,
): boolean {
  return selectedDates.some((dateStr) =>
    dayjs(dateStr).isBetween(start, end, "day", "[]"),
  );
}
